/**
 * @fileoverview JCKPQ03 - API LOG 查詢組件
 * @description 專門用於查詢和分析 API 日誌記錄的 Angular 組件
 * @author JCK Team
 * @version 1.0.0
 */

import { Component, Injector, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { idNameItem, JkBaseOption, MaterialModule, PageKind } from 'jk-service';
import { JkgridwinComponent, jkQueryComponent } from 'jk-advcom';
import { JackBaseComponentModule } from 'jk-service';

/**
 * JCKPQ03 查詢條件配置類別
 * @description 定義 API LOG 查詢的所有可配置參數和選項
 * @extends JkBaseOption 基礎選項類別，提供通用的查詢配置功能
 */
export class Jckpq03Option extends JkBaseOption {
  /** 查詢開始日期，格式：YYYY-MM-DD HH:mm:ss */
  startDate: string = '';

  /** 查詢結束日期，格式：YYYY-MM-DD HH:mm:ss */
  endDate: string = '';

  /** HTTP 方法篩選條件 */
  method: string = '';

  /** HTTP 狀態碼篩選條件 */
  statusCode: string = '';

  /** 請求 URL 篩選條件（支援模糊匹配） */
  url: string = '';

  /** 客戶端 IP 篩選條件（支援模糊匹配） */
  clientIp: string = '';

  /** 用戶 ID 篩選條件（支援模糊匹配） */
  userId: string = '';

  /** 當前頁碼 */
  page: string = '1';

  /** 每頁顯示筆數，預設 50，最大 1000 */
  pageSize: string = '50';

  /** 排序欄位名稱 */
  sortBy: string = 'timestamp';

  /** 排序方向：ASC (升序) 或 DESC (降序) */
  sortOrder: string = 'DESC';

  /** SQL ORDER BY 子句，預設按時間戳記降序排列 */
  orderBy: string = 'ORDER BY timestamp DESC';
}

/**
 * JCKPQ03 - API LOG 查詢組件
 * @description 主要的查詢組件類別，提供完整的 API 日誌查詢功能
 * @selector app-jckpq03
 * @standalone true
 * @templateUrl ./jckpq03.component.html
 * @styleUrls ./jckpq03.component.scss
 */
@Component({
  standalone: true,
  selector: 'app-jckpq03',
  templateUrl: './jckpq03.component.html',
  styleUrls: ['./jckpq03.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MaterialModule, JkgridwinComponent, JackBaseComponentModule],
})
export class JCKPQ03Component extends jkQueryComponent implements OnInit {
  /** 程式識別碼 */
  override _programId = 'JCKPQ03';

  /** 查詢條件配置實例 */
  filterOpt: Jckpq03Option = new Jckpq03Option(this.programId);

  /** HTTP 方法選項清單，用於下拉選單顯示 */
  methodList: idNameItem[] = [
    { id: '', name: '全部' },
    { id: 'GET', name: 'GET' },
    { id: 'POST', name: 'POST' },
    { id: 'PUT', name: 'PUT' },
    { id: 'DELETE', name: 'DELETE' },
    { id: 'PATCH', name: 'PATCH' }
  ];

  /** HTTP 狀態碼選項清單，用於下拉選單顯示 */
  statusCodeList: idNameItem[] = [
    { id: '', name: '全部' },
    { id: '200', name: '200 - OK' },
    { id: '201', name: '201 - Created' },
    { id: '400', name: '400 - Bad Request' },
    { id: '401', name: '401 - Unauthorized' },
    { id: '403', name: '403 - Forbidden' },
    { id: '404', name: '404 - Not Found' },
    { id: '500', name: '500 - Internal Server Error' }
  ];

  /** 排序欄位選項清單 */
  sortByList: idNameItem[] = [
    { id: 'CREATE_TIME', name: '建立時間' },
    { id: 'METHOD', name: 'HTTP方法' },
    { id: 'STATUS_CODE', name: '狀態碼' },
    { id: 'URL', name: '請求URL' },
    { id: 'CLIENT_IP', name: '客戶端IP' },
    { id: 'USER_ID', name: '用戶ID' }
  ];

  /** 排序方向選項清單 */
  sortOrderList: idNameItem[] = [
    { id: 'ASC', name: '升序' },
    { id: 'DESC', name: '降序' }
  ];

  /**
   * 建構函式
   * @param injector 依賴注入器，用於獲取 Angular 服務
   */
  constructor() {
    super();
    this.setFormOpt2(); // 初始化表單選項
  }

  /**
   * 組件初始化生命週期鉤子
   * @description Angular 組件初始化時呼叫，設定基本的組件狀態
   */
  override ngOnInit() {
    super.ngOnInit();
  }

  /**
   * 視圖初始化完成後的生命週期鉤子
   * @description 負責執行組件的初始化邏輯，包括查詢條件設定和下拉選單配置
   * @returns Promise<void>
   */
  override async ngAfterViewInit() {
    super.ngAfterViewInit();
    // JCKPQ03-S01 - API LOG 查詢主視窗

    try {
      // 初始化預設查詢條件（日期範圍等）
      await this.initializeQueryConditions();

      // 初始化下拉選單選項（HTTP方法、狀態碼等）
      this.initializeDropdownOptions();

      // 保留：主表查詢初始化（可選）
      //await this.masterLookup.init();
    } catch (error) {
      console.error('初始化失敗:', error);
      await this.showQueryMessage('組件初始化失敗', 'error');
    }

    // 檢查所有組件是否初始化完成 - 必須放在最後
    this.checkAllInit(this);
  }

  /**
   * 初始化查詢條件
   * @description 設定組件的預設查詢條件，包括日期範圍、排序方式和分頁設定
   * @returns Promise<void>
   * @throws Error 當初始化過程中發生錯誤時拋出
   */
  private async initializeQueryConditions(): Promise<void> {
    try {
      // 設定預設查詢條件 - 最近24小時
      const now = new Date();
      const twentyFourHoursAgo = new Date(now.getTime() - (24 * 60 * 60 * 1000));

      // 格式化日期並設定到查詢條件
      this.filterOpt.startDate = this.formatDate(twentyFourHoursAgo);
      this.filterOpt.endDate = this.formatDate(now);

      // 設定預設排序條件：按時間戳記降序排列（最新的記錄優先）
      this.filterOpt.sortBy = 'timestamp';
      this.filterOpt.sortOrder = 'DESC';

      // 設定預設分頁條件：第1頁，每頁50筆
      this.filterOpt.page = '1';
      this.filterOpt.pageSize = '50';

    } catch (error) {
      console.error('查詢條件初始化失敗:', error);
      throw error;
    }
  }

  /**
   * 初始化下拉選單選項
   * @description 配置所有下拉選單的選項資料，包括HTTP方法、狀態碼、排序欄位等
   * @returns void
   * @throws Error 當選項配置過程中發生錯誤時拋出
   */
  private initializeDropdownOptions(): void {
    try {
      // HTTP方法選項 - 包含常見的RESTful API方法
      this.methodList = [
        { id: '', name: '全部' },
        { id: 'GET', name: 'GET' },
        { id: 'POST', name: 'POST' },
        { id: 'PUT', name: 'PUT' },
        { id: 'DELETE', name: 'DELETE' },
        { id: 'PATCH', name: 'PATCH' },
        { id: 'HEAD', name: 'HEAD' },
        { id: 'OPTIONS', name: 'OPTIONS' }
      ];

      // HTTP狀態碼選項 - 包含常見的成功和錯誤狀態碼
      this.statusCodeList = [
        { id: '', name: '全部' },
        { id: '200', name: '200 - OK' },
        { id: '201', name: '201 - Created' },
        { id: '204', name: '204 - No Content' },
        { id: '400', name: '400 - Bad Request' },
        { id: '401', name: '401 - Unauthorized' },
        { id: '403', name: '403 - Forbidden' },
        { id: '404', name: '404 - Not Found' },
        { id: '405', name: '405 - Method Not Allowed' },
        { id: '422', name: '422 - Unprocessable Entity' },
        { id: '500', name: '500 - Internal Server Error' },
        { id: '502', name: '502 - Bad Gateway' },
        { id: '503', name: '503 - Service Unavailable' },
        { id: '504', name: '504 - Gateway Timeout' }
      ];

      // 排序欄位選項 - 對應資料庫欄位名稱
      this.sortByList = [
        { id: 'timestamp', name: '時間戳記' },
        { id: 'method', name: 'HTTP方法' },
        { id: 'status_code', name: '狀態碼' },
        { id: 'url', name: '請求URL' },
        { id: 'client_ip', name: '客戶端IP' },
        { id: 'user_id', name: '用戶ID' }
      ];

      // 排序方向選項 - 提供升序和降序選擇
      this.sortOrderList = [
        { id: 'DESC', name: '降序 (最新優先)' },
        { id: 'ASC', name: '升序 (最舊優先)' }
      ];

    } catch (error) {
      console.error('下拉選單初始化失敗:', error);
      throw error;
    }
  }

  /**
   * 組件銷毀生命週期鉤子
   * @description 組件銷毀時進行清理工作，釋放資源和取消訂閱
   * @returns void
   */
  ngOnExit() {
    // 清理資源和取消訂閱（如果有的話）
    // 釋放記憶體，清理事件監聽器等
  }

  /**
   * 查詢前置處理方法
   * @description 在執行查詢前進行參數設定和驗證，建構完整的 SQL 查詢條件
   * @returns Promise<boolean> 回傳 true 表示可以執行查詢，false 表示查詢被取消
   */
  override async beforeQuery(): Promise<boolean> {
    try {
      // 呼叫父類別的查詢前置處理
      await super.beforeQuery();

      // 設定網格初始化選項
      this.gridInitOpt = {
        pageKind: this.pageKind,           // 設定為報表類型
        programId: this.programId,           // 設定程式識別碼
        sortField: this.filterOpt.sortBy,    // 設定排序欄位
        sort: this.filterOpt.sortOrder.toLowerCase() as 'asc' | 'desc', // 設定排序方向
        defaultSort: null,                   // 不使用預設排序
      };

      // 設定查詢類型和視窗項目
      this.type = 'S';           // 查詢類型：S (Select)
      this.winItem = 'S01';      // 視窗項目：S01 (主查詢視窗)
      this.mainTable = 'JCKLC';  // 主表設定為 JCKLC (API LOG 資料表)

      // 建構 WHERE 條件子句 - 動態生成 SQL 查詢條件
      let whereConditions: string[] = [];
      whereConditions.push(' AND 1=1'); // 基礎條件，確保 WHERE 子句語法正確

      // 日期範圍條件 - 篩選指定時間範圍內的記錄
      if (this.filterOpt.startDate && this.filterOpt.startDate.trim()) {
        whereConditions.push(`timestamp >= '${this.filterOpt.startDate}'`);
      }
      if (this.filterOpt.endDate && this.filterOpt.endDate.trim()) {
        whereConditions.push(`timestamp <= '${this.filterOpt.endDate}'`);
      }

      // HTTP方法條件 - 精確匹配指定的 HTTP 方法
      if (this.filterOpt.method && this.filterOpt.method.trim()) {
        whereConditions.push(`method = '${this.filterOpt.method}'`);
      }

      // 狀態碼條件 - 精確匹配指定的 HTTP 狀態碼
      if (this.filterOpt.statusCode && this.filterOpt.statusCode.trim()) {
        whereConditions.push(`status_code = ${this.filterOpt.statusCode}`);
      }

      // URL條件 - 模糊匹配請求 URL
      if (this.filterOpt.url && this.filterOpt.url.trim()) {
        whereConditions.push(`url LIKE '%${this.filterOpt.url}%'`);
      }

      // 客戶端IP條件 - 模糊匹配客戶端 IP 位址
      if (this.filterOpt.clientIp && this.filterOpt.clientIp.trim()) {
        whereConditions.push(`client_ip LIKE '%${this.filterOpt.clientIp}%'`);
      }

      // 用戶ID條件 - 模糊匹配用戶識別碼
      if (this.filterOpt.userId && this.filterOpt.userId.trim()) {
        whereConditions.push(`user_id LIKE '%${this.filterOpt.userId}%'`);
      }

      // 合併所有 WHERE 條件，使用 AND 運算子連接
      this.where = whereConditions.join(' AND ');

      // 建構 ORDER BY 子句 - 設定結果排序方式
      let sortByField = this.filterOpt.sortBy;
      let sortOrder = this.filterOpt.sortOrder.toUpperCase();

      // 驗證排序方向的有效性
      if (sortOrder !== 'ASC' && sortOrder !== 'DESC') {
        sortOrder = 'DESC'; // 預設降序，顯示最新記錄優先
      }

      // 欄位名稱映射表 - 將前端欄位名稱對應到資料庫欄位名稱
      const validSortFields = {
        'timestamp': 'timestamp',
        'CREATE_TIME': 'timestamp',
        'method': 'method',
        'METHOD': 'method',
        'status_code': 'status_code',
        'STATUS_CODE': 'status_code',
        'url': 'url',
        'URL': 'url',
        'client_ip': 'client_ip',
        'CLIENT_IP': 'client_ip',
        'user_id': 'user_id',
        'USER_ID': 'user_id'
      };

      // 取得有效的排序欄位名稱，預設為 timestamp
      sortByField = validSortFields[sortByField as keyof typeof validSortFields] || 'timestamp';

      // 生成完整的 ORDER BY 子句
      this.order = `ORDER BY ${sortByField} ${sortOrder}`;

      // 處理分頁參數
      const page = parseInt(this.filterOpt.page) || 1;
      const pageSize = parseInt(this.filterOpt.pageSize) || 50;

      // 驗證分頁參數的有效性
      if (page < 1) {
        this.filterOpt.page = '1'; // 頁碼最小為 1
      }
      if (pageSize < 1 || pageSize > 1000) {
        this.filterOpt.pageSize = '50'; // 每頁筆數限制在 1-1000 之間
      }

      return true; // 查詢條件設定成功，可以執行查詢

    } catch (error) {
      console.error('beforeQuery 執行失敗:', error);
      this.showMessage('查詢條件設定失敗，請檢查輸入參數', 'error');
      return false; // 查詢條件設定失敗，中止查詢
    }
  }

  /**
   * 格式化日期為查詢用字串
   * @description 將 Date 物件格式化為資料庫查詢適用的字串格式
   * @param date 要格式化的日期物件
   * @returns string 格式化後的日期字串 (YYYY-MM-DD HH:mm:ss)
   */
  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  /**
   * 快速設定時間範圍
   * @description 快速設定查詢的日期範圍為最近 N 小時內的記錄
   * @param hours 過去的小時數 (正整數)
   * @returns Promise<void>
   * @example
   * // 設定查詢最近 1 小時的記錄
   * await setQuickTimeRange(1);
   * // 設定查詢最近 24 小時的記錄
   * await setQuickTimeRange(24);
   */
  async setQuickTimeRange(hours: number): Promise<void> {
    try {
      const now = new Date();
      // 計算指定小時數之前的時間點
      const pastTime = new Date(now.getTime() - (hours * 60 * 60 * 1000));

      // 設定查詢日期範圍
      this.filterOpt.startDate = this.formatDate(pastTime);
      this.filterOpt.endDate = this.formatDate(now);

      // 顯示操作成功訊息
      await this.showQueryMessage(`已設定查詢範圍為最近 ${hours} 小時`, 'info');
    } catch (error) {
      console.error('設定快速時間範圍失敗:', error);
      await this.showQueryMessage('設定時間範圍失敗', 'error');
    }
  }

  /**
   * 重置查詢條件
   * @description 將所有查詢條件重置為預設值，方便用戶重新開始查詢
   * @returns Promise<void>
   */
  async resetQueryConditions(): Promise<void> {
    try {
      // 重置篩選條件為空值
      this.filterOpt.method = '';
      this.filterOpt.statusCode = '';
      this.filterOpt.url = '';
      this.filterOpt.clientIp = '';
      this.filterOpt.userId = '';

      // 重新初始化日期範圍為預設值（最近24小時）
      await this.initializeQueryConditions();

      // 顯示操作成功訊息
      await this.showQueryMessage('查詢條件已重置', 'info');
    } catch (error) {
      console.error('重置查詢條件失敗:', error);
      await this.showQueryMessage('重置查詢條件失敗', 'error');
    }
  }

  /**
   * 驗證查詢條件
   * @description 在執行查詢前驗證所有輸入參數的有效性和安全性
   * @returns Promise<boolean> 驗證通過回傳 true，失敗回傳 false
   */
  private async validateQueryConditions(): Promise<boolean> {
    try {
      // 驗證日期範圍邏輯
      if (this.filterOpt.startDate && this.filterOpt.endDate) {
        const startDate = new Date(this.filterOpt.startDate);
        const endDate = new Date(this.filterOpt.endDate);

        // 檢查開始日期是否晚於結束日期
        if (startDate > endDate) {
          await this.showQueryMessage('開始日期不能晚於結束日期', 'warning');
          return false;
        }

        // 檢查日期範圍是否過大（效能考量）
        const timeDiff = endDate.getTime() - startDate.getTime();
        const daysDiff = timeDiff / (1000 * 60 * 60 * 24);
        if (daysDiff > 30) {
          await this.showQueryMessage('查詢日期範圍不能超過30天', 'warning');
          return false;
        }
      }

      // 驗證URL安全性 - 防止SQL注入
      if (this.filterOpt.url && this.filterOpt.url.trim()) {
        const url = this.filterOpt.url.trim();
        // 檢查是否包含潛在危險的SQL關鍵字
        if (url.includes('--') || url.includes(';')) {
          await this.showQueryMessage('URL包含不安全的字元', 'warning');
          return false;
        }
      }

      // 驗證IP位址安全性 - 防止SQL注入
      if (this.filterOpt.clientIp && this.filterOpt.clientIp.trim()) {
        const ip = this.filterOpt.clientIp.trim();
        // 檢查是否包含潛在危險的SQL關鍵字
        if (ip.includes('--') || ip.includes(';')) {
          await this.showQueryMessage('IP位址包含不安全的字元', 'warning');
          return false;
        }
      }

      return true; // 所有驗證都通過
    } catch (error) {
      console.error('查詢條件驗證失敗:', error);
      await this.showQueryMessage('查詢條件驗證失敗', 'error');
      return false;
    }
  }

  /**
   * 覆寫查詢方法，加入驗證
   * @description 覆寫父類別的查詢方法，在查詢前先進行條件驗證
   * @returns Promise<void>
   */
  override async doQuery(): Promise<void> {
    try {
      // 先進行查詢條件驗證
      if (!(await this.validateQueryConditions())) {
        return; // 驗證失敗，中止查詢
      }

      // 驗證通過，呼叫父類別的查詢方法
      await super.doQuery();
    } catch (error) {
      console.error('執行查詢失敗:', error);
      await this.showQueryMessage('執行查詢失敗', 'error');
    }
  }

  /**
   * 顯示查詢訊息的統一方法
   * @description 統一處理查詢相關的訊息顯示，包括控制台日誌和用戶界面提示
   * @param message 要顯示的訊息內容
   * @param type 訊息類型：'info' | 'warning' | 'error'
   * @returns Promise<void>
   */
  private async showQueryMessage(message: string, type: 'info' | 'warning' | 'error' = 'info'): Promise<void> {
    try {
      // 使用父類別的訊息顯示方法
      const alertType = type === 'warning' ? 'warning' : type;
      await this.showMessage(message, alertType as any);

      // 同時記錄到瀏覽器控制台，便於開發調試
      console.log(`[${type.toUpperCase()}] ${message}`);

      // 設定到組件的訊息屬性，供模板顯示
      this.logMessage = message;
    } catch (error) {
      console.error('顯示訊息失敗:', error);
    }
  }

  // ### [以上是必要的]================================
}
